// Imports ------------------------------------------------------------------->

import { Service, service, inject } from './di'
import { Flyer } from '../definitions/FlyerDefinitions'
import { Coupon } from '../definitions/CouponDefinitions'
import { EcomItemSummaryJSON } from '../definitions/ApiDefinitions'
import { StorageService } from './StorageService'
import { LocationService } from './LocationService'
import { FlyerService } from './FlyerService'
import { CouponService } from './CouponService'
import { AnalyticsService } from './AnalyticsService'
import { Gateflipp } from './GateflippService'
import { https } from '../utils/http'
import * as array from '../utils/array'
import { ApiService } from './ApiService'

// Private Definitions ------------------------------------------------------->

// Public Definitions -------------------------------------------------------->

// FIXME Search api isn't well wrapped, we need to get the flyer so we can
// grab the merchant logo.
export interface SearchItemAndFlyer {
    item: {
        left: number
        right: number
        top: number
        bottom: number
        flyer_id: number
        merchant_image_url: string
        flyer_item_id: number
        clipping_image_url: string
        name: string
    }
    flyer: Flyer
}

export type SearchModel = [
    Flyer[],
    SearchItemAndFlyer[],
    Coupon[],
    EcomItemSummaryJSON[],
]

@service
export class SearchService extends Service {
    @inject storage: StorageService
    @inject location: LocationService
    @inject flyerService: FlyerService
    @inject couponService: CouponService
    @inject analytics: AnalyticsService
    @inject gateflipp: Gateflipp
    @inject api: ApiService

    private _searchCache: { [key: string]: SearchModel } = {}
    private _searchPromiseCache: { [key: string]: Promise<SearchModel> } = {}

    getSearchResult(item: string) {
        if (
            !this._searchPromiseCache[item] ||
            !this._searchPromiseCache.hasOwnProperty(item)
        ) {
            this._searchPromiseCache[item] = this.gateflipp
                .search(this.location.postalCode, item)
                .then((results) => {
                    const foundMerchants = results.merchants
                    const flyerPromises = results.merchants.map((merchant) =>
                        this.flyerService.getFlyersByMerchantId(merchant.id)
                    ) as any
                    const itemPromises = results.items.map((item) =>
                        this.flyerService
                            .getFlyer(item.flyer_id)
                            .then((flyer) => {
                                if (!flyer) {
                                    // We have seen a case where /search endpoint returns flyer items that link to
                                    // a flyerid, BUT that flyer is NOT returned as part of the /data call.
                                    // As a result, flyer is NULL. For robustness, we need to flag that.
                                    // Such flyer item will, eventually, be skipped inside SearchPage.tsx
                                    // Unexpected! Flyer is NULL, but it should NOT be null
                                    // -------------------------------------------------------------------
                                }
                                return { item, flyer }
                            })
                    ) as any

                    const couponPromises = results.coupons.map((couponId) =>
                        this.couponService.getCoupon(couponId)
                    ) as any

                    return Promise.all([
                        Promise.all(flyerPromises).then((results) =>
                            array.flatten(results)
                        ),
                        Promise.all(itemPromises),
                        Promise.all(couponPromises),
                    ]).then(([flyers, items, coupons]) => {
                        // Go through every merchant that was returned by Backflipp XHR call.
                        // For each merchant, attempt to search for at least one flyer with that merchant id.
                        // If such a flyer exists -- great; move on to the next merchant.
                        // If not, then that is an indication that this merchant has a 'dark week'
                        // or is out of the distribution zone or we simply don't have their content.
                        // In that case, we need to create a special 'tombstone' flyer.
                        // This flyer will have a 'isTombstone' flag set to TRUE and
                        // will not have any properties other than:
                        // 'merchant', merchantId' and 'merchantLogo'.
                        // --------------------------------------------------------------
                        if (foundMerchants.length > 0) {
                            for (const merchantObj of foundMerchants) {
                                if (
                                    !array.find(
                                        flyers as Flyer[],
                                        (flyer: Flyer) =>
                                            flyer.merchantId === merchantObj.id
                                    )
                                ) {
                                    // None of the flyers we have belong to this merchant.
                                    // Add a tombstone flyer.
                                    // ----------------------------------------------------------
                                    const tombstone: Flyer = new Flyer({})
                                    tombstone.merchant = merchantObj.name
                                    tombstone.merchantId = merchantObj.id
                                    tombstone.merchantLogo = https(
                                        merchantObj.logo_url
                                    )
                                    tombstone.isTombstone = true
                                    ;(flyers as Flyer[]).push(tombstone)

                                    this.analytics.trackTombstone(
                                        item,
                                        tombstone.merchant,
                                        tombstone.merchantId
                                    )
                                }
                            }
                        }

                        return [flyers, items, coupons, results.ecom_items]
                    })
                })
                .then((results: any) => {
                    this._searchCache[item] = results
                    return results
                })
        }
        return this._searchPromiseCache[item]
    }

    hasSearchResult(item: string) {
        /* Note: need to verify that item property is on cache Object and not being picked up from the
         * prototype. For example, searching for 'watch' will actually return Object.watch on Firefox
         * because it's part of Objects prototype  */
        return this._searchCache.hasOwnProperty(item)
            ? this._searchCache[item]
            : null
    }
}
